import React, { Dispatch, memo, SetStateAction, useEffect, useState } from 'react';
import { Accordion, CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { LoanStatusType } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/types';
import { LayoutContainer } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/components/layoutContainer';
import LoanDetailsHeader from './LoanDetailsHeader';
import { errorSeverityMapping } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/utils/ErrorSeverityMapping';
import {
  groupErrorsBySeverity,
  sortSeverityKeys
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent/utils/filterDataUtils';
import { SeverityCode } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent/types';
import { ErrorListWithToast } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent/utils/copyItemsToClipBoard';
import { useLoansErrors } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent/hooks/useLoanErrors';
import { useQuery } from '@tanstack/react-query';
import { getIndividualLoanDetailsData } from './api/HbtMasterPoolLoansInventoryDetailService';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { SpinnerElement } from '../shared/ErrorMessage';
import ErrorMessage from '../HbtMasterPoolLoansInventoryTable/components/ErrorMessage';
import { HbtMaterPoolIndividualLoanDetailsMapper } from './Mapper/HbtIndividualLoanDetailsMapper';
import { MasterPoolLoanDetails } from './types';
import errorsMapping from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent/utils/errorsMapping';
import { renderEmptyDataState } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/RightPaneContent/utils/renderEmptyStates';
import { LoanInventoryTableFields } from '../HbtMasterPoolLoansInventoryTable/types';
import { ToastNotificationConfig } from '../HbtMasterPoolDetails/types';
interface HbtMasterPoolLoansInventoryDetailProps {
  loanReferenceNumber: string;
  loanPortfolioEpipProcessingStatus?: string;
  cmhcAccountNumber: number;
  loanInventoryData: LoanInventoryTableFields;
  setMasterPoolNotification?: (show: boolean) => void;
  setNotificationBannerConfig?: Dispatch<SetStateAction<ToastNotificationConfig>>;
  setIsForceStatusTriggered?: (isTriggered: boolean) => void;
}

const HbtMasterPoolLoansInventoryDetail = ({
  loanReferenceNumber,
  loanPortfolioEpipProcessingStatus,
  cmhcAccountNumber,
  loanInventoryData,
  setMasterPoolNotification,
  setNotificationBannerConfig,
  setIsForceStatusTriggered
}: HbtMasterPoolLoansInventoryDetailProps) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { t: mpd } = useTranslation('HbtMasterPool');
  const [loanDetails, setLoanDetails] = useState<MasterPoolLoanDetails>();
  const [isRequestDetailsCollapsed, setIsRequestDetailsCollapsed] = useState(true);
  const [isPropertyCollapsed, setIsPropertyCollapsed] = useState(false);
  const [isKeyLoanCollapsed, setIsKeyLoanCollapsed] = useState(false);
  const [isCmhcLowRatioCollapsed, setIsCmhcLowRatioCollapsed] = useState(false);
  const [isOtherLoanCollapsed, setIsOtherLoanCollapsed] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const authenticationContext = useAuthenticationContext();
  const [isIndividualLoanDetailLoading, setIsIndividualLoanDetailLoading] = useState<boolean>(true);
  const [loanStatus, setLoanStatus] = useState<LoanStatusType>('04');
  const [individualLoanDetailError, setIndividualLoanDetailError] = useState<string>('');
  const [openModifyFlyout, setModifyFlyout] = useState(false);
  const [openOverviewFlyout, setOverviewFlyout] = useState(false);

  const {
    borrowerLoanDetails = [],
    lenderDetails,
    propertyDetails,
    keyLoanDetails,
    cmhcLowRatioDetails,
    otherLoanData
  } = (loanDetails && HbtMaterPoolIndividualLoanDetailsMapper(loanDetails)) || {};

  const fetchData = async (): Promise<MasterPoolLoanDetails | null> => {
    const data = await getIndividualLoanDetailsData({
      authenticationContext,
      data: {
        cmhcAccountNumber
      }
    });
    return data;
  };

  const { data: loanDetailsData, isSuccess } = useQuery({
    queryKey: ['masterpoolLoanDetail', cmhcAccountNumber],
    queryFn: fetchData
  });

  const isReducedDataSet = loanDetailsData?.dataSetReducedIndicator === '1';

  useEffect(() => {
    if (!isSuccess || !loanDetailsData) return;
    if (loanDetailsData) {
      setIndividualLoanDetailError('');
    } else {
      setIndividualLoanDetailError('no_data_error');
    }
    setLoanStatus(loanDetailsData.accountStatusTypeCode as LoanStatusType);
    setLoanDetails(loanDetailsData);
    setIsIndividualLoanDetailLoading(false);
  }, [isSuccess, loanDetailsData as MasterPoolLoanDetails]);

  const handleToggle = () => {
    setCollapsed((prev) => !prev);
  };

  const { data: loans } = useLoansErrors();

  const mappedCodes = loanDetailsData
    ? errorsMapping(loanDetailsData?.errorMessages.join(), loans ?? [])
    : undefined;

  const renderErrorsAccordion = () => {
    const severityMapping = errorSeverityMapping(t);
    let groupedErrors = groupErrorsBySeverity(mappedCodes ?? {});
    const severityKeys = Object.keys(groupedErrors) as SeverityCode[];

    if (severityKeys.length === 0) {
      return renderEmptyDataState(
        t('Pi-Portfolio-Management-Empty-Data-Error-Header'),
        t('Master-Pool-Has-No-Errors')
      );
    }

    const sortedKeys = sortSeverityKeys(severityKeys, severityMapping);
    return (
      <Box className={styles?.contentAccordion}>
        {sortedKeys.map((severityKey) => (
          <Accordion
            key={severityKey}
            suppressBottomBorder={true}
            accordionData={{
              contentSlot: <ErrorListWithToast mappedErrors={groupedErrors[severityKey]!} />,
              contentType: 1,
              defaultExpanded: true,
              heading: `${severityMapping[severityKey]} (${
                Object.keys(groupedErrors[severityKey]!).length
              })`
            }}
          />
        ))}
      </Box>
    );
  };

  const requestDetailsSectionPropDetails = {
    isCollapsed: isRequestDetailsCollapsed,
    onToggle: () => setIsRequestDetailsCollapsed(!isRequestDetailsCollapsed),
    lenderDetails: lenderDetails,
    isToolTip: false
  };

  const propertyCharacteristicsSectionPropDetails = {
    className: styles.propertyContainer,
    isCollapsed: isPropertyCollapsed,
    onToggle: () => setIsPropertyCollapsed((prev) => !prev),
    propertyData: propertyDetails
  };

  const otherLoanCharacteristicsSectionPropDetails = loanDetails?.portfolioID
    ? {
        isCollapsed: isOtherLoanCollapsed,
        onToggle: () => setIsOtherLoanCollapsed(!isOtherLoanCollapsed),
        otherLoanData
      }
    : undefined;

  const keyLoanCharacteristicsSectionPropDetails = {
    isCollapsed: isKeyLoanCollapsed,
    onToggle: () => setIsKeyLoanCollapsed(!isKeyLoanCollapsed),
    keyLoanData: keyLoanDetails,
    options: {
      isToolTip: true,
      toolTipDesc: (function () {
        if (keyLoanDetails?.grandfathered === '0') {
          return mpd('Grandfathered-Indicator-0');
        } else if (keyLoanDetails?.grandfathered === '1') {
          return mpd('Grandfathered-Indicator-1');
        }
        return mpd('Grandfathered-Indicator-2');
      })()
    }
  };

  const cmhcLowRatioCharacteristicsSectionPropDetails = {
    isCollapsed: isCmhcLowRatioCollapsed,
    onToggle: () => setIsCmhcLowRatioCollapsed(!isCmhcLowRatioCollapsed),
    cmhcLowRatioData: cmhcLowRatioDetails,
    options: {
      LayoutContainerType: 'masterpool',
      isReducedDataSet: isReducedDataSet
    }
  };

  return (
    <>
      {isIndividualLoanDetailLoading && !individualLoanDetailError && <SpinnerElement />}
      {!isIndividualLoanDetailLoading && individualLoanDetailError && (
        <ErrorMessage errorType={individualLoanDetailError} />
      )}

      <Box className={styles.container}>
        {loanDetailsData && (
          <LoanDetailsHeader
            cmhcAccountNumber={cmhcAccountNumber}
            setModifyFlyout={setModifyFlyout}
            setOverviewFlyout={setOverviewFlyout}
            masterPoolNumber={loanDetails?.masterPoolCmhcAccountNumber}
            loanReferenceNumber={loanReferenceNumber}
            loanStatus={loanStatus}
            loanPortfolioEpipProcessingStatus={loanPortfolioEpipProcessingStatus}
            loanInventoryData={loanInventoryData}
            loanDetailsData={loanDetailsData}
            approvalDate={loanDetails?.applicationApprovedDate}
            cancellationDate={loanDetails?.applicationCancelledDate}
            setNotificationBannerConfig={setNotificationBannerConfig}
            setMasterPoolNotification={setMasterPoolNotification}
            setIsForceStatusTriggered={setIsForceStatusTriggered}
          />
        )}

        {/* Main Content Container */}
        <Box className={styles.mainContentContainer}>
          {/* Layout Container */}
          <Box className={styles.layoutWrapper}>
            <LayoutContainer
              borrowerDetails={borrowerLoanDetails}
              requestDetailsSectionPropDetails={requestDetailsSectionPropDetails}
              propertyCharacteristicsSectionPropDetails={propertyCharacteristicsSectionPropDetails}
              keyLoanCharacteristicsSectionPropDetails={keyLoanCharacteristicsSectionPropDetails}
              cmhcLowRatioCharacteristicsSectionPropDetails={
                cmhcLowRatioCharacteristicsSectionPropDetails
              }
              otherLoanCharacteristicsSectionPropDetails={
                otherLoanCharacteristicsSectionPropDetails
              }
              options={{ LayoutContainerType: 'masterpool' }}
            />
          </Box>

          {/* Right Side Panel */}
          {!collapsed ? (
            <Box className={styles.rightSidePanel}>
              <Box
                onClick={handleToggle}
                className={styles.toggleButton}
                aria-label={collapsed ? 'Expand Panel' : 'Collapse Panel'}
                sx={{
                  display: {
                    xs: 'flex',
                    md: 'flex',
                    lg: 'none'
                  }
                }}
              >
                <CmhcIcon
                  className={styles.accordionIcon}
                  size={CmhcIconSize.SMALL}
                  type={'icon_reduce'}
                />
              </Box>
              <Box className={styles.validationResultsContainer}>
                <Box className={styles.topBar}></Box>
                <Box className={styles.headerContainer}>
                  <Typography className={styles.headerTitle} variant="h6">
                    {t('Initial-Loan-Validation')}
                  </Typography>
                  <Typography className={styles.headerSubtitle} variant="h6">
                    {mpd('Master-Pool-Right-Pane-Body-Content')}
                  </Typography>
                </Box>
                <Box className={styles?.contentAccordion}>{renderErrorsAccordion()}</Box>
              </Box>
            </Box>
          ) : (
            <Box className={styles.collapsedStub}>
              <Box
                onClick={handleToggle}
                className={styles.toggleButton}
                aria-label={collapsed ? 'Expand Panel' : 'Collapse Panel'}
              >
                <CmhcIcon
                  className={styles.accordionIcon}
                  size={CmhcIconSize.SMALL}
                  type={'icon_enlarge'}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default memo(HbtMasterPoolLoansInventoryDetail);
